import { useState, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useApiClient } from '@/hooks/useApiClient';
import { useToast } from '@/contexts/ToastContext';
import { LoadingSpinner, PageError } from '@/components/ui';

interface PmsDocument {
  id: string;
  originalName: string;
  notes?: string;
  uploadedAt: string;
  uploadedByUserId: string;
}

// SVG Icons
const ClipboardIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
  </svg>
);

const CloudUploadIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const DocumentIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const PencilIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
  </svg>
);

const DownloadIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
  </svg>
);

const TrashIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const CheckCircleIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const InfoIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const FolderIcon = ({ className }: { className?: string }) => (
  <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

export default function PmsPage() {
  const { t, i18n } = useTranslation();
  const apiClient = useApiClient();
  const { addToast } = useToast();
  const queryClient = useQueryClient();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploading, setUploading] = useState(false);
  const [notes, setNotes] = useState('');
  const [editingDocId, setEditingDocId] = useState<string | null>(null);
  const [editingNotes, setEditingNotes] = useState('');

  // Auto-refresh interval (30 seconds)
  const REFRESH_INTERVAL = 30 * 1000;

  // Fetch all PMS documents
  const { data: documents = [], isLoading: isLoadingDocuments, error: documentsError } = useQuery({
    queryKey: ['pms-documents'],
    queryFn: async () => {
      const result = await apiClient.getPmsDocument();
      return Array.isArray(result) ? result : [];
    },
    refetchInterval: REFRESH_INTERVAL,
  });

  // Upload PMS mutation
  const uploadPmsMutation = useMutation({
    mutationFn: async (file: File) => {
      return apiClient.uploadPmsDocument(file, notes);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pms-documents'] });
      setNotes('');
      setUploading(false);
      addToast(t('pms.uploadSuccess'), 'success');
    },
    onError: (error: any) => {
      setUploading(false);
      addToast(error?.response?.data?.message || t('pms.uploadError'), 'error');
    },
  });

  // Delete PMS mutation
  const deletePmsMutation = useMutation({
    mutationFn: async (documentId: string) => {
      return apiClient.deletePmsDocument(documentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pms-documents'] });
      addToast(t('pms.deleteSuccess'), 'success');
    },
    onError: (error: any) => {
      addToast(error?.response?.data?.message || t('pms.deleteError'), 'error');
    },
  });

  // Download PMS mutation
  const downloadPmsMutation = useMutation({
    mutationFn: async (documentId: string) => {
      const result = await apiClient.getPmsDownloadUrl(documentId);
      if (result.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
    },
    onError: (error: any) => {
      addToast(error?.response?.data?.message || t('pms.downloadError'), 'error');
    },
  });

  // Update notes mutation
  const updateNotesMutation = useMutation({
    mutationFn: async ({ documentId, notes }: { documentId: string; notes: string }) => {
      return apiClient.updatePmsDocumentNotes(documentId, notes);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pms-documents'] });
      setEditingDocId(null);
      setEditingNotes('');
      addToast(t('pms.notesUpdated'), 'success');
    },
    onError: (error: any) => {
      addToast(error?.response?.data?.message || t('pms.notesUpdateError'), 'error');
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploading(true);
      uploadPmsMutation.mutate(file);
    }
  };

  const canUploadMore = documents.length < 5;
  const remainingSlots = 5 - documents.length;

  return (
    <div className="w-full max-w-5xl mx-auto px-4 sm:px-0">
      {/* Header */}
      <div className="mb-6 sm:mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl flex items-center justify-center shadow-lg">
            <ClipboardIcon className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-neutral-900">{t('pms.title')}</h1>
        </div>
        <p className="text-sm sm:text-base text-neutral-600 mt-2 ml-13 sm:ml-15">{t('pms.description')}</p>
      </div>

      {/* Stats Card */}
      <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl border-l-4 border-primary-500 shadow-lg p-4 sm:p-6 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${canUploadMore ? 'bg-accent-green/10' : 'bg-accent-orange/10'}`}>
              <FolderIcon className={`w-5 h-5 ${canUploadMore ? 'text-accent-green' : 'text-accent-orange'}`} />
            </div>
            <div>
              <p className="text-sm text-neutral-500 font-medium">{t('pms.documentsUploaded')}</p>
              <p className="text-2xl font-bold text-neutral-900">{documents.length}<span className="text-neutral-400">/5</span></p>
            </div>
          </div>
          <span className={`text-xs sm:text-sm font-semibold px-4 py-2 rounded-full ${canUploadMore ? 'bg-accent-green/10 text-accent-green' : 'bg-accent-orange/10 text-accent-orange'}`}>
            {canUploadMore
              ? t('pms.slotsAvailable', { count: remainingSlots })
              : t('pms.storageFull')}
          </span>
        </div>
      </div>

      {/* Upload Section */}
      <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-4 sm:p-6 md:p-8 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div className="flex items-center gap-2">
            <CloudUploadIcon className="w-5 h-5 text-primary-500" />
            <h2 className="text-base sm:text-lg font-bold text-neutral-900">{t('pms.uploadDocument')}</h2>
          </div>
        </div>

        {canUploadMore ? (
          <div className="space-y-4">
            <div
              className="border-2 border-dashed border-primary-200 rounded-xl p-6 sm:p-10 text-center cursor-pointer hover:border-primary-500 hover:bg-primary-50/50 transition-all duration-300 group"
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                onChange={handleFileChange}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png"
                hidden
                disabled={uploading}
              />
              <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                <CloudUploadIcon className="w-8 h-8 text-primary-600" />
              </div>
              <div className="text-sm sm:text-base text-neutral-900 font-semibold">{t('pms.clickToUpload')}</div>
              <div className="text-xs sm:text-sm text-neutral-500 mt-2">{t('pms.acceptedFormats')}</div>
              <div className="mt-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                <span>{t('pms.slotsRemaining', { count: remainingSlots })}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-neutral-700 mb-2">{t('pms.notesOptional')}</label>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('pms.notesPlaceholder')}
                className="w-full px-3 sm:px-4 py-3 rounded-xl border border-neutral-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 resize-none transition-all duration-200"
                rows={3}
                disabled={uploading}
              />
            </div>

            {uploading && (
              <div className="text-center py-4">
                <div className="inline-block">
                  <div className="animate-spin rounded-full h-10 w-10 border-2 border-primary-200 border-t-primary-600"></div>
                </div>
                <p className="text-neutral-600 mt-3 font-medium">{t('pms.uploading')}</p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <div className="w-16 h-16 mx-auto mb-4 bg-accent-orange/10 rounded-2xl flex items-center justify-center">
              <FolderIcon className="w-8 h-8 text-accent-orange" />
            </div>
            <p className="text-neutral-700 font-semibold">{t('pms.maxFilesReached')}</p>
            <p className="text-sm text-neutral-500 mt-1">{t('pms.deleteToUploadMore')}</p>
          </div>
        )}
      </div>

      {/* Uploaded Documents */}
      {isLoadingDocuments ? (
        <div className="mb-6"><LoadingSpinner message={t('pms.loading')} /></div>
      ) : documentsError ? (
        <div className="mb-6"><PageError message={t('pms.loadError')} /></div>
      ) : documents.length > 0 ? (
        <div className="bg-gradient-to-br from-white via-neutral-50 to-neutral-100 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 p-4 sm:p-6 mb-6">
          <div className="flex items-center gap-2 mb-6">
            <DocumentIcon className="w-5 h-5 text-primary-500" />
            <h2 className="text-base sm:text-lg font-bold text-neutral-900">{t('pms.uploadedDocuments')}</h2>
          </div>

          <div className="space-y-4">
            {documents.map((doc: PmsDocument) => (
              <div key={doc.id} className="p-4 sm:p-5 rounded-xl bg-white border border-neutral-100 shadow-sm hover:shadow-md transition-all duration-200">
                <div className="flex items-start justify-between mb-4 gap-2">
                  <div className="flex items-start gap-3 flex-1 min-w-0">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-100 to-primary-200 flex items-center justify-center flex-shrink-0">
                      <DocumentIcon className="w-5 h-5 text-primary-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-neutral-900 text-sm sm:text-base truncate">{doc.originalName}</div>
                      <div className="text-xs text-neutral-500 mt-1 flex items-center gap-1">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {t('pms.uploadedAt')} {new Date(doc.uploadedAt).toLocaleString(i18n.language)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                {editingDocId === doc.id ? (
                  <div className="mb-4 space-y-3">
                    <textarea
                      value={editingNotes}
                      onChange={(e) => setEditingNotes(e.target.value)}
                      placeholder={t('pms.editNotesPlaceholder')}
                      className="w-full px-3 py-2 rounded-lg border border-neutral-300 text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                      rows={2}
                    />
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() =>
                          updateNotesMutation.mutate({ documentId: doc.id, notes: editingNotes })
                        }
                        disabled={updateNotesMutation.isPending}
                        className="flex items-center justify-center gap-1 px-4 py-2 text-xs sm:text-sm rounded-lg bg-gradient-to-r from-accent-green to-green-600 text-white font-medium hover:from-green-600 hover:to-green-700 disabled:opacity-50 transition-all duration-200 shadow-sm"
                      >
                        <CheckCircleIcon className="w-4 h-4" />
                        {t('pms.save')}
                      </button>
                      <button
                        onClick={() => setEditingDocId(null)}
                        className="px-4 py-2 text-xs sm:text-sm rounded-lg bg-neutral-200 text-neutral-700 font-medium hover:bg-neutral-300 transition-colors"
                      >
                        {t('pms.cancel')}
                      </button>
                    </div>
                  </div>
                ) : doc.notes ? (
                  <div
                    onClick={() => {
                      setEditingDocId(doc.id);
                      setEditingNotes(doc.notes || '');
                    }}
                    className="mb-4 p-3 bg-gradient-to-r from-neutral-50 to-white rounded-lg border border-neutral-200 text-xs sm:text-sm text-neutral-700 cursor-pointer hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-200 break-words"
                  >
                    <div className="flex items-start gap-2">
                      <PencilIcon className="w-4 h-4 text-neutral-400 flex-shrink-0 mt-0.5" />
                      <span><strong className="text-neutral-800">{t('pms.notes')}:</strong> {doc.notes}</span>
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => {
                      setEditingDocId(doc.id);
                      setEditingNotes('');
                    }}
                    className="mb-4 p-3 bg-neutral-50 rounded-lg border-2 border-dashed border-neutral-200 text-xs sm:text-sm text-neutral-500 cursor-pointer hover:border-primary-300 hover:bg-primary-50/30 transition-all duration-200 flex items-center gap-2"
                  >
                    <PencilIcon className="w-4 h-4" />
                    {t('pms.clickToAddNotes')}
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-2">
                  <button
                    onClick={() => downloadPmsMutation.mutate(doc.id)}
                    disabled={downloadPmsMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gradient-to-r from-primary-500 to-primary-600 text-white text-xs sm:text-sm font-semibold hover:from-primary-600 hover:to-primary-700 disabled:opacity-50 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <DownloadIcon className="w-4 h-4" />
                    {downloadPmsMutation.isPending ? t('pms.downloading') : t('pms.download')}
                  </button>
                  <button
                    onClick={() => {
                      if (window.confirm(t('pms.deleteConfirm'))) {
                        deletePmsMutation.mutate(doc.id);
                      }
                    }}
                    disabled={deletePmsMutation.isPending}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg bg-gradient-to-r from-accent-red to-red-600 text-white text-xs sm:text-sm font-semibold hover:from-red-600 hover:to-red-700 disabled:opacity-50 transition-all duration-200 shadow-sm hover:shadow-md"
                  >
                    <TrashIcon className="w-4 h-4" />
                    {deletePmsMutation.isPending ? t('pms.deleting') : t('pms.delete')}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-gradient-to-br from-primary-50 to-primary-100/50 border border-primary-200 rounded-xl p-6 sm:p-8 mb-6 shadow-sm">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-primary-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <DocumentIcon className="w-6 h-6 text-primary-600" />
            </div>
            <div>
              <div className="text-base sm:text-lg text-primary-900 font-semibold">{t('pms.noDocuments')}</div>
              <p className="text-sm text-primary-700 mt-1">{t('pms.noDocumentsDescription')}</p>
            </div>
          </div>
        </div>
      )}

      {/* Info Section */}
      <div className="mt-6 sm:mt-8 bg-gradient-to-br from-white via-neutral-50 to-neutral-100 border-l-4 border-accent-green rounded-xl shadow-lg p-4 sm:p-6">
        <div className="flex items-center gap-2 mb-4">
          <InfoIcon className="w-5 h-5 text-accent-green" />
          <h3 className="font-bold text-sm sm:text-base text-neutral-900">{t('pms.infoTitle')}</h3>
        </div>
        <ul className="space-y-3 text-xs sm:text-sm text-neutral-700">
          <li className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-accent-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircleIcon className="w-3.5 h-3.5 text-accent-green" />
            </div>
            <span>{t('pms.info1')}</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-accent-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircleIcon className="w-3.5 h-3.5 text-accent-green" />
            </div>
            <span>{t('pms.info2')}</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-accent-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircleIcon className="w-3.5 h-3.5 text-accent-green" />
            </div>
            <span>{t('pms.info3')}</span>
          </li>
          <li className="flex items-start gap-3">
            <div className="w-5 h-5 rounded-full bg-accent-green/10 flex items-center justify-center flex-shrink-0 mt-0.5">
              <CheckCircleIcon className="w-3.5 h-3.5 text-accent-green" />
            </div>
            <span>{t('pms.info4')}</span>
          </li>
        </ul>
      </div>
    </div>
  );
}
